import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const PasswordReset = ({ apiClient }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    reset_token: searchParams.get('token') || '',
    new_password: '',
    confirm_password: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await apiClient.post('/auth/reset-password', {
        reset_token: formData.reset_token,
        new_password: formData.new_password
      });
      
      toast.success('Password reset successfully! You can now log in with your new password.');
      navigate('/login');
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100">
            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Reset Password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your new password below
          </p>
        </div>

        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Hidden token field - automatically populated from URL */}
            <input
              name="reset_token"
              type="hidden"
              value={formData.reset_token}
              onChange={handleInputChange}
            />

            <div>
              <label htmlFor="new_password" className="form-label">
                New Password
              </label>
              <input
                id="new_password"
                name="new_password"
                type="password"
                required
                data-testid="new-password-input"
                className="form-input"
                placeholder="Enter new password"
                value={formData.new_password}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label htmlFor="confirm_password" className="form-label">
                Confirm Password
              </label>
              <input
                id="confirm_password"
                name="confirm_password"
                type="password"
                required
                data-testid="confirm-password-input"
                className="form-input"
                placeholder="Confirm new password"
                value={formData.confirm_password}
                onChange={handleInputChange}
              />
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading}
                data-testid="reset-password-submit-button"
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <div className="spinner mx-auto"></div> : 'Reset Password'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/login')}
                data-testid="back-to-login-button"
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400"
              >
                Back to Login
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;